import { createHash, createPublicKey, type JsonWebKey } from 'node:crypto';
import { z } from 'zod';
import { VirtualPathSchema } from './files.js';
export const SftpNameSchema = z.string().regex(/^[a-z0-9_-]{3,32}$/);
export const SftpNameInputSchema = z.object({ name: SftpNameSchema }).strict();
export const SshKeyIdSchema = z.object({ id: z.string().uuid() }).strict();
export const MAX_SFTP_BYTES = 4 * 1024 ** 3;
export const SFTP_CHUNK_BYTES = 1024 ** 2;
export function splitSftpLogin(login: string) {
  const match = /^([a-z0-9_-]{3,32})\.([a-z0-9_-]+)$/.exec(login);
  if (!match) throw new Error('Invalid SFTP login');
  return { name: match[1]!, serverId: match[2]! };
}
function unsignedMpint(value: Buffer) {
  if (!value.length || value[0]! & 0x80 || (value[0] === 0 && !(value[1]! & 0x80))) {
    throw new Error('Invalid positive SSH mpint');
  }
  return value[0] === 0 ? value.subarray(1) : value;
}
export function parseSshPublicKey(input: string) {
  const match = /^(ssh-ed25519|ecdsa-sha2-nistp(?:256|384|521)|ssh-rsa) ([A-Za-z0-9+/]+={0,2})(?: [^\r\n]*)?$/.exec(
    input.trim(),
  );
  if (!match) throw new Error('OpenSSH public key required');
  const blob = Buffer.from(match[2]!, 'base64');
  let offset = 0;
  const field = () => {
    if (offset + 4 > blob.length) throw new Error('Invalid key');
    const size = blob.readUInt32BE(offset);
    offset += 4;
    if (size > blob.length - offset) throw new Error('Invalid key');
    const value = blob.subarray(offset, offset + size);
    offset += size;
    return value;
  };
  const type = field().toString();
  if (type !== match[1]) throw new Error('Key type mismatch');
  let jwk: JsonWebKey;
  if (type === 'ssh-ed25519') {
    const key = field();
    if (key.length !== 32) throw new Error('Invalid ed25519 key');
    jwk = { kty: 'OKP', crv: 'Ed25519', x: key.toString('base64url') };
  } else if (type === 'ssh-rsa') {
    const e = unsignedMpint(field());
    const unsigned = unsignedMpint(field());
    const bits = unsigned.length * 8 - Math.clz32(unsigned[0] ?? 0) + 24;
    if (bits < 3072) throw new Error('RSA requires 3072 bits');
    jwk = { kty: 'RSA', e: e.toString('base64url'), n: unsigned.toString('base64url') };
  } else {
    const curve = field().toString();
    if (type !== `ecdsa-sha2-${curve}`) throw new Error('Curve mismatch');
    const point = field();
    const size = ({ nistp256: 32, nistp384: 48, nistp521: 66 } as Record<string, number>)[curve];
    if (!size || point.length !== 1 + 2 * size || point[0] !== 4) throw new Error('Invalid EC key');
    jwk = {
      kty: 'EC',
      crv: ({ nistp256: 'P-256', nistp384: 'P-384', nistp521: 'P-521' } as Record<string, string>)[curve],
      x: point.subarray(1, 1 + size).toString('base64url'),
      y: point.subarray(1 + size).toString('base64url'),
    };
  }
  if (offset !== blob.length) throw new Error('Trailing key data');
  createPublicKey({ key: jwk, format: 'jwk' });
  return {
    publicKey: `${type} ${blob.toString('base64')}`,
    fingerprint: `SHA256:${createHash('sha256').update(blob).digest('base64').replace(/=+$/, '')}`,
  };
}
export const SshKeyInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    publicKey: z
      .string()
      .max(16384)
      .superRefine((value, ctx) => {
        try {
          parseSshPublicKey(value);
        } catch {
          ctx.addIssue({ code: 'custom', message: 'Invalid OpenSSH key (RSA >= 3072 bits)' });
        }
      }),
  })
  .strict();
export const SshKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  publicKey: z.string(),
  fingerprint: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export const SftpSettingsSchema = z.object({
  name: SftpNameSchema.nullable(),
  host: z.string(),
  port: z.number().int().nullable(),
  fingerprint: z.string().nullable(),
});
export type SftpSettings = z.infer<typeof SftpSettingsSchema>;
export type SshKey = z.infer<typeof SshKeySchema>;
export const SftpOpenResultSchema = z.object({ handle: z.string().uuid() }).strict();
export const SftpStatSchema = z
  .object({ size: z.number().int().nonnegative(), directory: z.boolean(), modified: z.number().int().nonnegative() })
  .strict();
export const SftpReadResultSchema = z.object({ data: z.string().max(1398104) }).strict();
const handle = z.string().uuid();
const offset = z.number().int().min(0).max(MAX_SFTP_BYTES);
const envelope = { id: z.string().min(1), serverId: z.string().min(1) };
export const SftpAgentSchemas = [
  z
    .object({
      ...envelope,
      type: z.literal('fs_open'),
      path: VirtualPathSchema,
      write: z.boolean(),
      create: z.boolean(),
      truncate: z.boolean(),
      exclusive: z.boolean(),
    })
    .strict(),
  z
    .object({
      ...envelope,
      type: z.literal('fs_read_range'),
      handle,
      offset,
      length: z.number().int().min(1).max(SFTP_CHUNK_BYTES),
    })
    .strict(),
  z
    .object({
      ...envelope,
      type: z.literal('fs_write_range'),
      handle,
      offset,
      data: z
        .string()
        .max(1398104)
        .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/)
        .refine((v) => Buffer.from(v, 'base64').length <= SFTP_CHUNK_BYTES),
    })
    .strict(),
  z.object({ ...envelope, type: z.literal('fs_close'), handle, commit: z.boolean() }).strict(),
  z.object({ ...envelope, type: z.literal('fs_stat'), path: VirtualPathSchema }).strict(),
  z.object({ ...envelope, type: z.literal('fs_fstat'), handle }).strict(),
  z
    .object({ ...envelope, type: z.literal('fs_rename'), path: VirtualPathSchema, destination: VirtualPathSchema })
    .strict(),
] as const;
type WithoutEnvelope<T> = T extends unknown ? Omit<T, 'id' | 'serverId'> : never;
export type SftpFileOp = WithoutEnvelope<z.infer<(typeof SftpAgentSchemas)[number]>>;
